import { createContext, type CSSProperties, type ReactNode, useCallback, useContext, useEffect, useId, useRef, useState } from 'react';
import { type Lang, LANGUAGES, perLang, setLang, t, useT } from '../i18n';
import { IconAlert, IconCheck, IconX } from './icons';

// ------------------------------------------------------------------ campo de formulario
interface FieldProps {
  label: string;
  hint?: ReactNode;
  error?: string;
  /** Muestra "n / máx" junto a la etiqueta. */
  count?: { value: string; max: number };
  children: (id: string) => ReactNode;
  className?: string;
}

export function Field({ label, hint, error, count, children, className }: FieldProps) {
  const id = useId();
  return (
    <div className={`field ${className ?? ''}`}>
      <label htmlFor={id}>
        <span>{label}</span>
        {count && (
          <span className={`counter ${count.value.length > count.max ? 'over' : ''}`}>
            {count.value.length} / {count.max}
          </span>
        )}
      </label>
      {children(id)}
      {error ? <div className="error">{error}</div> : hint ? <div className="hint">{hint}</div> : null}
    </div>
  );
}

/** Deja el espacio de la etiqueta para que un botón quede a la altura de los campos de su fila. */
export function FieldAligned({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return (
    <div className="field" style={style}>
      <span className="field-label" aria-hidden="true">
        &nbsp;
      </span>
      <div className="field-control">{children}</div>
    </div>
  );
}

export function Switch({ checked, onChange, label, disabled }: { checked: boolean; onChange: (v: boolean) => void; label: string; disabled?: boolean }) {
  return (
    <label className={`switch${disabled ? ' is-disabled' : ''}`}>
      <input type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
      <span>{label}</span>
    </label>
  );
}

export function Spinner({ label }: { label?: string }) {
  const { t } = useT();
  return (
    <div className="row" role="status">
      <div className="spinner" />
      <span className="muted">{label ?? t('Cargando…')}</span>
    </div>
  );
}

/**
 * El idioma del dashboard (seis): arranca en el del país desde el que se conecta el navegador (o en
 * el del navegador) y lo que se elija aquí se recuerda en este navegador y manda sobre lo automático.
 * Cerrado muestra sólo el código (ES, PL…); al abrirlo, el nombre de cada idioma en su idioma.
 */
export function LangSwitch({ className }: { className?: string }) {
  const { lang, t } = useT();
  const cur = LANGUAGES.find((l) => l.code === lang) ?? LANGUAGES[0];
  return (
    <label className={`lang-switch ${className ?? ''}`} title={t('Idioma')}>
      <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
        <circle cx="8" cy="8" r="6.25" />
        <path d="M1.75 8h12.5M8 1.75c1.8 1.9 2.7 4 2.7 6.25S9.8 12.35 8 14.25M8 1.75C6.2 3.65 5.3 5.75 5.3 8s.9 4.35 2.7 6.25" />
      </svg>
      <span aria-hidden="true">{cur.label}</span>
      <select value={lang} onChange={(e) => setLang(e.target.value as Lang)} aria-label={t('Idioma')}>
        {LANGUAGES.map((l) => (
          <option key={l.code} value={l.code} lang={l.code}>
            {l.name}
          </option>
        ))}
      </select>
    </label>
  );
}

export function PageHead({ kicker, title, description, children }: { kicker: string; title: ReactNode; description?: ReactNode; children?: ReactNode }) {
  return (
    <header className="page-head">
      <div>
        <p className="kicker">{kicker}</p>
        <h1>{title}</h1>
        {description && <p>{description}</p>}
      </div>
      {children && <div className="actions">{children}</div>}
    </header>
  );
}

// ------------------------------------------------------------------ diálogo
export function Modal({
  title,
  onClose,
  children,
  footer,
  wide,
  dismissible = true,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
  /** false: no se cierra con Escape, con la X ni tocando afuera (hay que elegir una opción). */
  dismissible?: boolean;
}) {
  const { t } = useT();
  const ref = useRef<HTMLDivElement>(null);
  const close = useRef(onClose);
  useEffect(() => {
    close.current = onClose;
  });
  // El foco va al primer campo solo al abrir (no en cada render, o saltaría mientras se escribe).
  useEffect(() => {
    ref.current?.querySelector<HTMLElement>('input:not([readonly]),textarea,select,button')?.focus();
  }, []);
  useEffect(() => {
    if (!dismissible) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && close.current();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [dismissible]);
  return (
    <div className="modal-backdrop" onMouseDown={(e) => dismissible && e.target === e.currentTarget && onClose()}>
      <div className="modal" role="dialog" aria-modal="true" aria-label={title} ref={ref} style={wide ? { width: 'min(760px, 100%)' } : undefined}>
        <header className="row between">
          <h2>{title}</h2>
          {dismissible && (
            <button className="btn btn-ghost btn-icon" onClick={onClose} aria-label={t('Cerrar')}>
              <IconX />
            </button>
          )}
        </header>
        <div className="body">{children}</div>
        {footer && <footer>{footer}</footer>}
      </div>
    </div>
  );
}

/** Confirmación con promesa: `if (await confirm('¿Borrar?')) …` */
interface ConfirmRequest {
  message: ReactNode;
  /** Sin etiqueta: "Confirmar" (en el idioma del momento). */
  confirmLabel?: string;
  danger: boolean;
  resolve: (v: boolean) => void;
}

// ------------------------------------------------------------------ avisos (toasts) y confirmaciones
interface Toast {
  id: number;
  kind: 'ok' | 'error' | 'info';
  text: string;
}

interface Feedback {
  toast: (text: string, kind?: Toast['kind']) => void;
  confirm: (message: ReactNode, opts?: { confirmLabel?: string; danger?: boolean }) => Promise<boolean>;
}

const FeedbackContext = createContext<Feedback | null>(null);

export function FeedbackProvider({ children }: { children: ReactNode }) {
  const { t } = useT();
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [pending, setPending] = useState<ConfirmRequest | null>(null);

  const toast = useCallback((text: string, kind: Toast['kind'] = 'ok') => {
    const id = Date.now() + Math.random();
    setToasts((t) => [...t, { id, kind, text }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), kind === 'error' ? 6500 : 3500);
  }, []);

  const confirm = useCallback<Feedback['confirm']>(
    (message, opts) =>
      new Promise((resolve) =>
        setPending({ message, confirmLabel: opts?.confirmLabel, danger: opts?.danger ?? false, resolve }),
      ),
    [],
  );

  const close = (v: boolean) => {
    pending?.resolve(v);
    setPending(null);
  };

  return (
    <FeedbackContext.Provider value={{ toast, confirm }}>
      {children}
      <div className="toasts" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className={`toast ${t.kind}`}>
            <div className="row">
              {t.kind === 'error' ? <IconAlert width={18} /> : t.kind === 'ok' ? <IconCheck width={18} /> : null}
              <span>{t.text}</span>
            </div>
          </div>
        ))}
      </div>
      {pending && (
        <Modal
          title={t('Confirmar')}
          onClose={() => close(false)}
          footer={
            <>
              <button className="btn" onClick={() => close(false)}>
                {t('Cancelar')}
              </button>
              <button className={`btn ${pending.danger ? 'btn-danger' : 'btn-primary'}`} onClick={() => close(true)}>
                {pending.confirmLabel ?? t('Confirmar')}
              </button>
            </>
          }
        >
          <div>{pending.message}</div>
        </Modal>
      )}
    </FeedbackContext.Provider>
  );
}

export function useFeedback(): Feedback {
  const ctx = useContext(FeedbackContext);
  if (!ctx) throw new Error('FeedbackProvider falta');
  return ctx;
}

// ------------------------------------------------------------------ utilidades de formato
// En el idioma del dashboard (español: "9 oct 2026, 14:05"; inglés: "9 Oct 2026, 14:05"). Quien las
// usa al pintar ya se vuelve a pintar al cambiar el idioma (usa useT()).
const dateFmt = perLang((locale) => new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }));
export const formatDate = (iso: string | null | undefined) => (iso ? dateFmt().format(new Date(iso)) : '—');

const rtf = perLang((locale) => new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }));
export function timeAgo(iso: string): string {
  const s = (new Date(iso).getTime() - Date.now()) / 1000;
  const abs = Math.abs(s);
  if (abs < 60) return t('ahora');
  if (abs < 3600) return rtf().format(Math.round(s / 60), 'minute');
  if (abs < 86400) return rtf().format(Math.round(s / 3600), 'hour');
  if (abs < 86400 * 7) return rtf().format(Math.round(s / 86400), 'day');
  return formatDate(iso);
}

export const formatBytes = (n: number | null | undefined) =>
  n == null ? '' : n < 1024 * 1024 ? `${Math.round(n / 1024)} KB` : `${(n / 1024 / 1024).toFixed(1)} MB`;

/** "Rojo metálico" → "rojo-metalico" */
export const slugify = (s: string) =>
  s
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
